import { Matrix, Text } from "pixi.js";
import { graphic } from "../Graphic";
import { selection } from "../editions/selection/Selection";
import { containerEvents } from "../ContainerEvents";
import { EntityService } from "./EntityService";
import { SelectionEditionMode } from "../editions/selection/SelectionEditionMode";
import { GraphicItemService } from "./row/GraphicItemService";
import { Shape } from "../helpers/Shape";

export class Entity {

    static GRAPHIC_STATE_BOOKED = 'booked';
    static GRAPHIC_STATE_DISABLED = 'disabled';
    static GRAPHIC_STATE_FREE = 'free';

    static getBoundsEntities(entities) {
        const bounds = { x: { min: null, max: null }, y: { min: null, max: null } };
        entities.forEach(entity => {
            const points = entity.getBoundsPoints();
            points.forEach(point => {
                if (!bounds.x.min || point.x < bounds.x.min) bounds.x.min = point.x;
                if (!bounds.y.min || point.y < bounds.y.min) bounds.y.min = point.y;
                if (!bounds.x.max || point.x > bounds.x.max) bounds.x.max = point.x;
                if (!bounds.y.max || point.y > bounds.y.max) bounds.y.max = point.y;
            })
        });
        return {
            ...bounds,
            width: bounds.x.max - bounds.x.min,
            height: bounds.y.max - bounds.y.min
        };
    }

    constructor({ id = null, container, graphicItems, type, enableCacheAsBitmap = false }) {
        this.graphicItems = graphicItems;
        this.enableCacheAsBitmap = enableCacheAsBitmap;
        this.graphicItems.forEach(gi => gi.cacheAsBitmap = this.enableCacheAsBitmap);
        this.container = container;
        this.container.interactive = true;
        this.id = id;
        this.type = type;
        this.isInMouseSelectionArea = false;
        this.isEnable = true;
        this.graphicItemsEvents = [];
        this.selectedSeatIndex = [];
        this.disabledSeatIndex = [];
    }

    get isSelected() {
        return selection.entities.includes(this)
    }

    getProps() {
        const { width, height, worldTransform } = this.container;
        const { a, d, b, c, tx, ty } = worldTransform;

        const position = graphic.withTransformContainer({
            x: tx,
            y: ty
        });

        const props = {
            transformationMatrix: {
                a, b, c, d,
                e: position.x,
                f: position.y
            },
            type: this.type,
            width,
            height
        };

        if (this.id) props.id = this.id;

        return props;
    }

    updateFromProps(props, bindProps = null, entityFromProps, bindEntity) {
        if (bindProps !== null) props = Object.assign(props, bindProps);
        this.redraw(() => {
            const newEntity = entityFromProps(props);
            this.container = newEntity.container;
            this.id = newEntity.id;
            this.graphicItems = newEntity.graphicItems;
            bindEntity(newEntity);
        });
    }

    draw(level = null, graphicsContainer = this.container) {
        this.graphicItems.forEach(graphicItem => graphicsContainer.addChild(graphicItem));

        if (level !== null) graphic.container.addChildAt(this.container, level);
        else graphic.container.addChild(this.container);

        this.onClickEntityBind = this.onClickEntity.bind(this);
        this.onPointerUpBind = this.onPointerUp.bind(this);
        this.onOutEntityBind = this.onOutEntity.bind(this);
        this.onOverEntityBind = this.onOverEntity.bind(this);
        this.container.on('pointerdown', this.onClickEntityBind);
        this.container.on('pointerup', this.onPointerUpBind);
        this.container.on('pointerover', this.onOverEntityBind);
        this.container.on('pointerout', this.onOutEntityBind);
    }

    redraw(bindProps = null) {
        const currentParent = this.container.parent;

        if (this.isSelected) this.clearSelectedArea();
        this.destroy();

        if (bindProps !== null) bindProps();

        this.draw();
        if (this.isSelected) this.drawSelectedArea();

        currentParent.addChildAt(this.container, 0);
    }

    destroy(graphicsContainer = this.container) {
        this.graphicItems.forEach(graphicItem => graphicsContainer.removeChild(graphicItem));
        this.container.parent.removeChild(this.container);

        this.container.off('pointerdown', this.onClickEntityBind);
        this.container.off('pointerover', this.onOverEntityBind);
        this.container.off('pointerout', this.onOutEntityBind);
        this.container.off('pointerup', this.onPointerUpBind);

        const index = containerEvents.overEntities.indexOf(this);
        if (index !== -1) containerEvents.overEntities.splice(index, 1);
    }

    onClickEntity() {
        if (graphic.containEditionMode(SelectionEditionMode)) {
            if (selection.entities.includes(this) && containerEvents.isCtrlPressed) {
                selection.unselect([this]);
            }
            if (selection.entities.length > 0 && !containerEvents.isCtrlPressed && !selection.entities.includes(this)) {
                selection.destroy();
            }
            if (!selection.entities.includes(this)) {
                selection.select([this]);
            }
        }
    }

    onPointerUp() {

    }

    onOverEntity() {
        containerEvents.overEntities.push(this);
    }

    onOutEntity() {
        const index = containerEvents.overEntities.indexOf(this);
        if (index !== -1) containerEvents.overEntities.splice(index, 1);
    }

    moveInWorldSpace(level = null, scalable = false) {
        let currentWorldTransform = this.container.worldTransform;

        let a = scalable ? currentWorldTransform.a / graphic.zoom : currentWorldTransform.a;
        let d = scalable ? currentWorldTransform.d / graphic.zoom : currentWorldTransform.d;

        let b = currentWorldTransform.b;
        let c = currentWorldTransform.c;

        let { x: e, y: f } = graphic.withTransformContainer({ x: currentWorldTransform.tx, y: currentWorldTransform.ty });

        if (level !== null) graphic.container.addChildAt(this.container, level);
        else this.container.setParent(graphic.container);

        const matrix = new Matrix();
        matrix.set(a, b, c, d, e, f);
        this.container.transform.setFromMatrix(matrix);

        if (!scalable) this.container.scale = {x: 1, y: 1};
    }

    getBounds(local = false) {
        const bounds = { x: { min: null, max: null }, y: { min: null, max: null } };
        this.graphicItems.forEach(graphicItem => {
            let boundsItem = local ? graphicItem.getLocalBounds() : graphicItem.getBounds();
            if (local) {
                boundsItem.x += graphicItem.x;
                boundsItem.y += graphicItem.y;
            }
            if (!local) {
                const localBoundsItem = graphic.withTransformContainer({ x: boundsItem.x, y: boundsItem.y });
                boundsItem.x = localBoundsItem.x;
                boundsItem.y = localBoundsItem.y;
                boundsItem.width = (boundsItem.width / graphic.zoom);
                boundsItem.height = (boundsItem.height / graphic.zoom);
            }
            if (!bounds.x.min || boundsItem.x < bounds.x.min) bounds.x.min = boundsItem.x;
            if (!bounds.y.min || boundsItem.y < bounds.y.min) bounds.y.min = boundsItem.y;
            if (!bounds.x.max || boundsItem.x + boundsItem.width > bounds.x.max) bounds.x.max = boundsItem.x + boundsItem.width;
            if (!bounds.y.max || boundsItem.y + boundsItem.height > bounds.y.max) bounds.y.max = boundsItem.y + boundsItem.height;
        });
        return {
            ...bounds,
            width: bounds.x.max - bounds.x.min,
            height: bounds.y.max - bounds.y.min
        };
    }

    getBoundsPoints(local = false) {
        const boundsProps = this.getBounds(local);
        return [
            { x: boundsProps.x.min, y: boundsProps.y.min },
            { x: boundsProps.x.max, y: boundsProps.y.min },
            { x: boundsProps.x.max, y: boundsProps.y.max },
            { x: boundsProps.x.min, y: boundsProps.y.max }
        ];
    }

    drawSelectedArea() {
        if (this.selectedArea) this.clearSelectedArea();

        this.graphicItems.forEach(gi => gi.cacheAsBitmap = false);
        this.selectedArea = Shape.createAbstractShape(this.getBoundsPoints(true), [1, 0x6F9FD8, 1], [0x6F9FD8, 0.2]);
        this.container.addChild(this.selectedArea);
    }

    clearSelectedArea() {
        if (!this.selectedArea) return;

        this.graphicItems.forEach(gi => gi.cacheAsBitmap = this.enableCacheAsBitmap);
        this.container.removeChild(this.selectedArea);
        this.selectedArea.destroy();
        this.selectedArea = null;
    }

    onUnselectEntity() {
        this.graphicItems.forEach(gi => gi.cacheAsBitmap = this.enableCacheAsBitmap);
    }

    onSelectEntity() {
        this.graphicItems.forEach(gi => gi.cacheAsBitmap = false);
    }

    isDisabledGraphicItem(index) {
        return this.disabledSeatIndex.includes(index)
    }

    disableGraphicItem(index, state) {
        if (!this.disabledSeatIndex.includes(index)) {
            if (!this.category) return;
            this.graphicItems[index].status = state;
            switch (state) {
                case Entity.GRAPHIC_STATE_DISABLED: {
                    EntityService.invalidateGraphicItem(this.graphicItems[index]);
                    break;
                }
                case Entity.GRAPHIC_STATE_BOOKED:
                default: {
                    GraphicItemService.setGraphicItemColor(this.graphicItems[index], this.category.color, 0.15);
                    if (this.graphicItems[index].children[0] instanceof Text) {
                        this.graphicItems[index].children[0].text = '';
                    }
                }
            }
            this.graphicItems[index].interactive = true;
            this.disabledSeatIndex.push(index);
        }
    }

    enableGraphicItem(index) {
        const i = this.disabledSeatIndex.indexOf(index);
        if (i !== -1) {
            this.disabledSeatIndex.splice(i, 1);
            this.graphicItems[index].cursor = 'pointer';
            GraphicItemService.setGraphicItemColor(this.graphicItems[index], this.category.color);
        }
    }

    enable() {
        this.container.cursor = 'pointer';
        this.isEnable = true;
        this.graphicItems.forEach((_, index) => {
            if (!this.isDisabledGraphicItem(index)) this.enableGraphicItem(index)
        });
    }

    disable() {
        this.container.cursor = 'not-allowed';
        this.isEnable = false;
        this.graphicItems.forEach((_, index) => this.disableGraphicItem(index));
    }
}